/**
 * Mobile drawer secondary currency: trigger opens a dialog sheet that is a **sibling** of
 * `.menu-drawer` (not inside the transformed panel). The sheet uses `position: fixed` and is sized to
 * `menuDrawer.getBoundingClientRect()` so the overlay always matches the **visible** drawer when L1
 * accordions add scrollable height.
 */
function parseDrawerAnimationMs() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--drawer-animation-speed').trim();
  if (!raw) return 300;
  if (raw.endsWith('ms')) return Math.max(0, Number.parseFloat(raw)) || 300;
  if (raw.endsWith('s')) return Math.max(0, (Number.parseFloat(raw) || 0.3) * 1000);
  return 300;
}

/**
 * @param {HTMLElement | null} menuDrawer
 * @param {HTMLElement} sheet
 */
function syncSheetFrame(menuDrawer, sheet) {
  if (!menuDrawer) return;
  const r = menuDrawer.getBoundingClientRect();
  const top = Math.max(0, r.top);
  const left = Math.max(0, r.left);
  const w = Math.max(0, r.width);
  const h = Math.max(0, r.height);
  sheet.style.setProperty('--drawer-secondary-sheet-top', `${top}px`);
  sheet.style.setProperty('--drawer-secondary-sheet-left', `${left}px`);
  sheet.style.setProperty('--drawer-secondary-sheet-width', `${w}px`);
  sheet.style.setProperty('--drawer-secondary-sheet-height', `${h}px`);
}

function initDrawerSecondaryCurrencySheet() {
  const trigger = document.getElementById('DrawerSecondaryCurrencyTrigger');
  const sheet = document.getElementById('DrawerSecondaryCurrencySheet');
  if (!trigger || !sheet) return;

  const menuDrawer = trigger.closest('details')?.querySelector('[data-header-drawer]') ?? null;
  const closeButton = sheet.querySelector('.drawer-secondary-nav__close-button');
  const backdrop = sheet.querySelector('.drawer-secondary-currency-sheet__backdrop');

  if (!menuDrawer) return;
  if (!sheet.querySelector('.drawer-secondary-nav__localization-panel')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationDuration = reducedMotion ? 1 : parseDrawerAnimationMs();

  let isAnimating = false;

  const isOpen = () => !sheet.hasAttribute('hidden');

  const VISIBLE_CLASS = 'drawer-secondary-currency-sheet--visible';

  const requestFrameSync = () => {
    if (!isOpen()) return;
    syncSheetFrame(menuDrawer, sheet);
  };

  window.addEventListener('resize', requestFrameSync, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', requestFrameSync);
    window.visualViewport.addEventListener('scroll', requestFrameSync);
  }

  const resizeMenuDrawer = new ResizeObserver(() => {
    requestFrameSync();
  });
  resizeMenuDrawer.observe(menuDrawer);

  function openSheet() {
    if (isAnimating || isOpen()) return;

    isAnimating = true;
    sheet.removeAttribute('hidden');
    sheet.classList.remove(VISIBLE_CLASS);
    trigger.setAttribute('aria-expanded', 'true');

    syncSheetFrame(menuDrawer, sheet);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncSheetFrame(menuDrawer, sheet);
        sheet.classList.add(VISIBLE_CLASS);
        closeButton?.focus({ preventScroll: true });
      });
    });

    window.setTimeout(() => {
      isAnimating = false;
    }, animationDuration);
  }

  function finishClose() {
    sheet.setAttribute('hidden', '');
    sheet.classList.remove(VISIBLE_CLASS);
    trigger.setAttribute('aria-expanded', 'false');
    isAnimating = false;
    trigger.focus({ preventScroll: true });
  }

  function closeSheet() {
    if (!isOpen()) return;
    if (isAnimating) return;

    isAnimating = true;
    sheet.classList.remove(VISIBLE_CLASS);

    window.setTimeout(finishClose, animationDuration);
  }

  trigger.addEventListener('click', () => {
    if (isOpen()) {
      closeSheet();
    } else {
      openSheet();
    }
  });

  closeButton?.addEventListener('click', (e) => {
    e.preventDefault();
    closeSheet();
  });

  backdrop?.addEventListener('click', () => {
    closeSheet();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      closeSheet();
    }
  });

  const drawerDetails = menuDrawer.closest('details');
  drawerDetails?.addEventListener('toggle', () => {
    if (!drawerDetails.open && isOpen()) {
      sheet.setAttribute('hidden', '');
      sheet.classList.remove(VISIBLE_CLASS);
      trigger.setAttribute('aria-expanded', 'false');
      isAnimating = false;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDrawerSecondaryCurrencySheet, { once: true });
} else {
  initDrawerSecondaryCurrencySheet();
}
